import 'components/ordering/multiple-baskets/basket-selector/basket-selector-basket.js'
import { CSSResultGroup, html, LitElement, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { BASKET_API, BasketApi } from 'components/ordering/multiple-baskets/basket-api/basket-api.js'
import { SelectBasketRequest } from 'components/ordering/multiple-baskets/basket-api/select-basket.js'
import { User } from 'components/ordering/multiple-baskets/basket-api/user.js'
import { SelectingBasketEvent } from 'components/ordering/multiple-baskets/basket-events.js'
import {
  BASKET_LIST_MUTATOR,
  BASKET_LIST_STORE,
  BasketListMutator,
  BasketListStore,
} from 'components/ordering/multiple-baskets/basket-store/basket-list-store.js'
import { BasketListItem } from 'components/ordering/multiple-baskets/basket-store/basket-list-parser.js'
import { style } from './basket-selector.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'


@customElement('csc-basket-selector')
export class CSCBasketSelector extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @state()
  private data: BasketListItem[] = []

  @property({ attribute: 'ordering-endpoint' })
  orderingEndpoint = ''

  @property({ attribute: 'user-id', type: Number })
  userId: number | null = null

  @property({ attribute: 'party-id', type: Number })
  partyId: number | null = null

  @property({ attribute: 'shop-id', type: Number })
  shopId: number | null = null

  @property({ attribute: 'show-cumulated-count', type: Boolean })
  showCumulatedCount = false

  @property({ attribute: 'basket-index-url' })
  basketIndexUrl = ''

  @inject(BASKET_API)
  private basketApi!: BasketApi

  @inject(BASKET_LIST_MUTATOR)
  private basketListMutator!: BasketListMutator

  @inject(BASKET_LIST_STORE)
  private basketListStore!: BasketListStore

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  private onSelectingBasket = (selectingBasketEvent: SelectingBasketEvent) => {
    const targetBasket = selectingBasketEvent.targetBasket

    if (targetBasket.selectedState !== 'selectable') { return }

    this.basketApi.selectBasket(
      new SelectBasketRequest(targetBasket.basketId, this.orderingEndpoint, this.user.shopId, this.user.userId, this.user.partyId)
    ).then(() => {
      this.windowLocation.reload()
    })

    this.data.forEach((basketListItem: BasketListItem) => {
      if (basketListItem === targetBasket) {
        basketListItem.selectedState = 'loading'
      } else {
        basketListItem.selectedState = 'selectable'
      }
    })

    this.requestUpdate()
  }

  private get user(): User {
    if (this.userId !== null && this.partyId !== null && this.shopId !== null) {
      return { userId: this.userId, shopId: this.shopId, partyId: this.partyId }
    }
    throw new Error('user-id, party-id and shop-id are mandatory.')
  }

  protected override render(): TemplateResult {
    return html`
      <div class="basket-selector-list">${this.data.map(this.renderBasket)}</div>`
  }

  private renderBasket = (basket: BasketListItem): TemplateResult => {
    return html`
      <cse-selectable state="${basket.selectedState}">
        <csc-basket-selector-basket
          .basket="${basket}"
          ?show-cumulated-count=${this.showCumulatedCount}
          ordering-endpoint="${this.orderingEndpoint}"
          @selecting-basket="${this.onSelectingBasket}"
          basket-index-url="${this.basketIndexUrl}">
        </csc-basket-selector-basket>
      </cse-selectable>
    `
  }

  override connectedCallback() {
    super.connectedCallback()

    createEffect(() => {
      this.data = this.basketListStore.basketList()
    })

    this.basketListMutator.initialize(this.user)
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'csc-basket-selector': CSCBasketSelector
  }
}
